import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { projectsApi } from '../../services/apiService';
import { SOCKET_URL } from '../../config/api';
import './AdminProjectDetailPage.css';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const statusConfig = {
  draft: { label: 'Draft', color: '#6D6D6D', bg: '#E5E5E5' },
  pending_approval: { label: 'Pending', color: '#92400E', bg: '#FEF3C7' },
  approved: { label: 'Approved', color: '#1E40AF', bg: '#DBEAFE' },
  published: { label: 'Published', color: '#006928', bg: '#DCFCE7' },
  unpublished: { label: 'Unpublished', color: '#6D6D6D', bg: '#E5E5E5' },
  rejected: { label: 'Rejected', color: '#DC2626', bg: '#FEE2E2' },
  archived: { label: 'Archived', color: '#6D6D6D', bg: '#E5E5E5' },
  completed: { label: 'Completed', color: '#006928', bg: '#DCFCE7' },
};

const AdminProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const data = await projectsApi.getByIdAdmin(id);
      setProject(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchProject();
  }, [id, fetchProject]);

  const handleStatusChange = async (newStatus) => {
    if (!project || statusLoading) return;
    try {
      setStatusLoading(true);
      const actionMap = {
        approved: () => projectsApi.approve(project.id),
        published: () => projectsApi.publish(project.id),
        unpublished: () => projectsApi.unpublish(project.id),
        archived: () => projectsApi.archive(project.id),
        rejected: () => projectsApi.reject(project.id),
      };
      if (actionMap[newStatus]) {
        await actionMap[newStatus]();
      } else {
        await projectsApi.updateStatus(project.id, newStatus);
      }
      setProject(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await projectsApi.delete(project.id);
      navigate('/admin/projects');
    } catch (err) {
      console.error('Failed to delete project:', err);
    }
  };

  const parseImages = (imagesStr) => {
    if (!imagesStr) return [];
    try { return JSON.parse(imagesStr); } catch { return []; }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="apd-content">
          <div className="apd-loading">
            <div className="apd-spinner" />
            <p>Loading project details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !project) {
    return (
      <AdminLayout>
        <div className="apd-content">
          <div className="apd-error">
            <p>{error || 'Project not found.'}</p>
            <button onClick={() => navigate('/admin/projects')}>Back to Projects</button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const sConf = statusConfig[project.status] || statusConfig.draft;
  const images = parseImages(project.images);

  return (
    <AdminLayout>
      <div className="apd-content">
        {/* Top bar */}
        <div className="apd-top-bar">
          <button className="apd-back-btn" onClick={() => navigate('/admin/projects')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Projects
          </button>
          <div className="apd-top-actions">
            <select
              className="apd-status-select"
              value={project.status || 'draft'}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={statusLoading}
            >
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending</option>
              <option value="approved">Approved</option>
              <option value="published">Published</option>
              <option value="unpublished">Unpublished</option>
              <option value="rejected">Rejected</option>
              <option value="archived">Archived</option>
              <option value="completed">Completed</option>
            </select>
            <button className="apd-edit-btn" onClick={() => navigate(`/project-input?edit=${project.id}`)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Edit
            </button>
            <button className="apd-delete-btn" onClick={() => setShowDeleteConfirm(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Delete
            </button>
          </div>
        </div>

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div className="apd-delete-confirm">
            <p>Are you sure you want to delete this project?</p>
            <div className="apd-delete-confirm-actions">
              <button className="apd-confirm-del" onClick={handleDelete}>Yes, Delete</button>
              <button className="apd-cancel-del" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Header card */}
        <div className="apd-header-card">
          <div className="apd-header-left">
            {project.image ? (
              <img className="apd-project-image" src={getImageUrl(project.image)} alt={project.title} />
            ) : (
              <div className="apd-image-placeholder">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/><path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            )}
            <div className="apd-header-info">
              <h1 className="apd-title">{project.title}</h1>
              {project.companyName && <p className="apd-company">{project.companyName}</p>}
              <div className="apd-header-meta">
                <span className="apd-status-badge" style={{ color: sConf.color, background: sConf.bg }}>
                  {sConf.label}
                </span>
                {project.projectType && <span className="apd-type-badge">{project.projectType}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Details grid */}
        <div className="apd-details-grid">
          <div className="apd-card">
            <h3 className="apd-card-title">Project Information</h3>
            <div className="apd-field-list">
              <div className="apd-field">
                <span className="apd-label">Type</span>
                <span className="apd-value">{project.projectType || '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Sub Category</span>
                <span className="apd-value">{project.subCategory || '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Budget</span>
                <span className="apd-value">{project.budget ? `${project.currency || 'USD'} ${project.budget.toLocaleString()}` : '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Start Date</span>
                <span className="apd-value">{project.startDate ? new Date(project.startDate).toLocaleDateString() : '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">End Date</span>
                <span className="apd-value">{project.endDate ? new Date(project.endDate).toLocaleDateString() : '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Views</span>
                <span className="apd-value">{project.views || 0}</span>
              </div>
            </div>
          </div>

          <div className="apd-card">
            <h3 className="apd-card-title">Location & Contact</h3>
            <div className="apd-field-list">
              <div className="apd-field">
                <span className="apd-label">Country</span>
                <span className="apd-value">{project.country || '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">City</span>
                <span className="apd-value">{project.city || '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Address</span>
                <span className="apd-value">{project.address || '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Contact Name</span>
                <span className="apd-value">{project.contactName || '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Contact Email</span>
                <span className="apd-value">{project.contactEmail || '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Contact Phone</span>
                <span className="apd-value">{project.contactPhone || '—'}</span>
              </div>
            </div>
          </div>

          <div className="apd-card">
            <h3 className="apd-card-title">Metadata</h3>
            <div className="apd-field-list">
              <div className="apd-field">
                <span className="apd-label">ID</span>
                <span className="apd-value">{project.id}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Slug</span>
                <span className="apd-value">{project.slug || '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Specialist ID</span>
                <span className="apd-value">{project.specialistId || '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Created</span>
                <span className="apd-value">{project.createdAt ? new Date(project.createdAt).toLocaleDateString() : '—'}</span>
              </div>
              <div className="apd-field">
                <span className="apd-label">Updated</span>
                <span className="apd-value">{project.updatedAt ? new Date(project.updatedAt).toLocaleDateString() : '—'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {project.description && (
          <div className="apd-card apd-desc-card">
            <h3 className="apd-card-title">Description</h3>
            <p className="apd-desc-text">{project.description}</p>
          </div>
        )}

        {/* Images */}
        {(project.image || images.length > 0) && (
          <div className="apd-card apd-images-card">
            <h3 className="apd-card-title">Images</h3>
            <div className="apd-images-grid">
              {project.image && <img className="apd-img-thumb" src={getImageUrl(project.image)} alt="Main" />}
              {images.map((img, i) => (
                <img key={i} className="apd-img-thumb" src={getImageUrl(img)} alt={`Image ${i + 1}`} />
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProjectDetailPage;
